
let sessionExpiredHandled = false;

function toPlainHeaders(headers) {
    if (!headers) return {};
    if (headers instanceof Headers) return Object.fromEntries(headers.entries());
    if (Array.isArray(headers)) return Object.fromEntries(headers);
    return { ...headers };
}

function isSameOrigin(url) {
    try {
        return new URL(url, window.location.href).origin === window.location.origin;
    } catch {
        return false;
    }
}

function halt() {
    return new Promise(() => {});
}

function navigateOnce(action) {
    if (sessionExpiredHandled) return true;
    sessionExpiredHandled = true;
    action();
    return true;
}

export async function fetchWithRedirectGuard(url, options) {
    const opts = {
        ...options,
        headers: {
            ...toPlainHeaders(options?.headers),
            "X-Requested-With": "XMLHttpRequest",
            // Tells the server which page this call came from. See note below - the server
            // only acts on this if SessionExpiryPolicy is wired to read it.
            "X-Current-Page": window.location.pathname + window.location.search,
        },
    };

    const res = await fetch(url, opts);

    // 401 = the session is gone. The server has already decided whether we should reload
    // where we are or go somewhere specific.
    if (res.status === 401) {
        const action = res.headers.get("X-Auth-Action");
        const target = res.headers.get("X-Auth-Redirect");

        if (action === "redirect" && target && isSameOrigin(target)) {
            navigateOnce(() => {
                window.location.href = target;
            });
            return halt();
        }

        if (action === "reload" || !action) {
            // replace() rather than reload(): if this page was itself reached by a POST,
            // reload() re-triggers the form resubmission dialog. replace() also avoids
            // stacking a history entry, so Back still works.
            //
            // No action header at all is treated as a reload rather than ignored - a 401
            // from anywhere else in the stack still means "you are not logged in", and
            // reloading surfaces that instead of failing silently.
            navigateOnce(() => {
                window.location.replace(window.location.href);
            });
            return halt();
        }

        // A 401 we do not recognise, or a redirect target that is not ours: hand it back so
        // the caller sees a real failure rather than hanging on a promise that never
        // settles.
        console.warn("Unhandled 401", { action, target });
        return res;
    }

    // Genuine GET redirect that fetch followed transparently (non-auth). Should not happen
    // on the expiry path, since X-Requested-With makes the server answer with headers
    // rather than a 302 - this is the safety net for anything that slips through.
    if (res.redirected && isSameOrigin(res.url)) {
        navigateOnce(() => {
            window.location.href = res.url;
        });
        return halt();
    }

    return res;
}
